import React from 'react';
import { motion } from 'framer-motion';
import { Building, Shield, Users, TrendingUp, Target } from 'lucide-react';
export function MunicipalScalePage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1082">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1083">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1084">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1085">
            <span className="w-8 h-px bg-teal" data-id="element-1086"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1087">
              Scale
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1088"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1089">
            Municipal Scale
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1090">
            At the municipal scale, NCJI is designed to support local civic
            resilience and community strengthening. It helps local leaders,
            public institutions, and community systems better understand the
            human capability patterns shaping neighborhood outcomes, local
            trust, opportunity pathways, and civic stability.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1091">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1092">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1093">
            This allows cities and local governments to move beyond reactive
            problem management toward a more structured understanding of the
            human layer beneath public life.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1094">
            Municipal systems are often closest to the real conditions people
            experience day to day. They see where youth pathways are weak, where
            misinformation spreads easily, where local support systems are
            fragmented, and where communities may lack the capability structures
            that support long-term resilience. NCJI helps make those patterns
            more visible so local institutions can respond more intentionally.
          </p>
        </motion.div>
      </section>

      {/* Local Civic Resilience */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1095">
        <div className="max-w-4xl mx-auto" data-id="element-1096">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1097">
            <div className="flex items-center gap-4 mb-6" data-id="element-1098">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1099">
                <Shield size={24} data-id="element-1100" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1101">
                Local Civic Resilience
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1102">
              NCJI can help local systems better understand the capabilities
              that support resilience at the community level. These include
              reasoning quality, manipulation resistance, emotional regulation,
              cooperation, self-direction, and local leadership depth. By
              understanding where these are strong or weak, municipalities can
              build better interventions and stronger support systems over time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Youth Pathways and Opportunity Access */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1103">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1104">
          <div className="flex items-center gap-4 mb-6" data-id="element-1105">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1106">
              <TrendingUp size={24} data-id="element-1107" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1108">
              Youth Pathways and Opportunity Access
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1109">
            At the local level, one of the most important long-term questions is
            whether young people are being connected to real developmental
            pathways. NCJI can help identify where youth capability is strong
            but under-supported, where local opportunity alignment is weak, and
            where community systems need to do more to connect potential to
            education, mentorship, work, and civic participation.
          </p>
        </motion.div>
      </section>

      {/* Community Strengthening */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1110">
        <div className="max-w-4xl mx-auto" data-id="element-1111">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1112">
            <div className="flex items-center gap-4 mb-6" data-id="element-1113">
              <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1114">
                <Building size={24} data-id="element-1115" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1116">
                Community Strengthening
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1117">
              Municipal strength depends on more than infrastructure. It also
              depends on whether communities have local trust, functioning
              support systems, healthy participation, and pathways for people to
              develop into constructive roles. NCJI helps local leaders better
              understand where these conditions are holding and where
              communities may be vulnerable to fragmentation, instability, or
              underdevelopment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Local Leadership and Coordination */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1118">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1119">
          <div className="flex items-center gap-4 mb-6" data-id="element-1120">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1121">
              <Users size={24} data-id="element-1122" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1123">
              Local Leadership and Coordination
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1124">
            Cities and municipalities rely on local leaders who can think
            clearly, cooperate well, and carry responsibility without worsening
            instability. NCJI can help support local leadership development by
            identifying where community leadership pipelines are shallow, where
            stronger readiness is needed, and where support structures can help
            cultivate more capable local responsibility over time.
          </p>
        </motion.div>
      </section>
    </div>;
}